import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Star,
  User,
  Clock,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Award,
  TrendingUp,
  Activity,
  Users,
  DollarSign,
  BookOpen,
  MessageSquare,
  Edit,
  Shield,
  AlertCircle,
  Eye,
  Download,
  Filter,
  Search,
  Crown,
  Heart,
  CreditCard,
  Package
} from 'lucide-react'
import { format } from 'date-fns'
import ApiService from '../services/api'
import toast from 'react-hot-toast'

function YajmanDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [yajman, setYajman] = useState(null)
  const [bookings, setBookings] = useState([])
  const [stats, setStats] = useState({
    totalBookings: 0,
    completedBookings: 0,
    pendingBookings: 0,
    cancelledBookings: 0,
    totalSpent: 0,
    averageBookingValue: 0,
    favoriteServices: [],
    memberSince: null
  })
  const [loading, setLoading] = useState(true)
  const [bookingsLoading, setBookingsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [bookingFilter, setBookingFilter] = useState('all')
  const [bookingSearch, setBookingSearch] = useState('')

  useEffect(() => {
    if (id) {
      fetchYajmanDetails()
      fetchYajmanBookings()
    }
  }, [id])

  const fetchYajmanDetails = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await ApiService.getYajmanById(id)
      setYajman(response.data)

      // Calculate stats from yajman data
      const totalBookings = response.data.totalBookings || 0
      const completedBookings = response.data.completedBookings || 0
      const cancelledBookings = response.data.cancelledBookings || 0
      const totalSpent = response.data.totalSpent || 0
      const pendingBookings = totalBookings - completedBookings - cancelledBookings
      const averageBookingValue = completedBookings > 0 ? Math.round(totalSpent / completedBookings) : 0

      const yajmanStats = {
        totalBookings,
        completedBookings,
        pendingBookings: pendingBookings > 0 ? pendingBookings : 0,
        cancelledBookings,
        totalSpent,
        averageBookingValue,
        favoriteServices: response.data.favoriteServices || [],
        memberSince: response.data.createdAt
      }
      setStats(yajmanStats)
    } catch (error) {
      console.error('Error fetching yajman details:', error)
      setError(error.message || 'Failed to load yajman details')
      toast.error('Failed to load yajman details')
    } finally {
      setLoading(false)
    }
  }

  const fetchYajmanBookings = async () => {
    try {
      setBookingsLoading(true)
      const response = await ApiService.getYajmanBookings(id)
      setBookings(response.data || [])
    } catch (error) {
      console.error('Error fetching yajman bookings:', error)
      toast.error('Failed to load bookings')
      setBookings([])
    } finally {
      setBookingsLoading(false)
    }
  }

  const handleStatusUpdate = async (newStatus) => {
    try {
      await ApiService.updateYajmanStatus(id, newStatus)
      setYajman(prev => ({ ...prev, status: newStatus }))
      toast.success(`Yajman status updated to ${newStatus.toLowerCase()}`)
    } catch (error) {
      console.error('Error updating yajman status:', error)
      toast.error('Failed to update status')
    }
  }

  const filteredBookings = bookings.filter(booking => {
    const matchesFilter = bookingFilter === 'all' || booking.status?.toLowerCase() === bookingFilter
    const matchesSearch = !bookingSearch ||
      booking.service?.name?.toLowerCase().includes(bookingSearch.toLowerCase()) ||
      booking.pandit?.name?.toLowerCase().includes(bookingSearch.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'confirmed': return 'bg-blue-100 text-blue-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !yajman) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Yajman</h2>
          <p className="text-gray-600 mb-4">{error || 'Yajman not found'}</p>
          <button
            onClick={() => navigate('/yajman')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Yajman List
          </button>
        </div>
      </div>
    )
  }

  const statCards = [
    {
      title: 'Total Bookings',
      value: stats.totalBookings,
      icon: BookOpen,
      color: 'bg-blue-500',
      description: 'All time bookings'
    },
    {
      title: 'Completed',
      value: stats.completedBookings,
      icon: CheckCircle,
      color: 'bg-green-500',
      description: 'Successfully completed'
    },
    {
      title: 'Total Spent',
      value: `₹${stats.totalSpent.toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-purple-500',
      description: 'Lifetime spending'
    },
    {
      title: 'Avg. Booking Value',
      value: `₹${stats.averageBookingValue.toLocaleString()}`,
      icon: TrendingUp,
      color: 'bg-orange-500',
      description: 'Per booking average'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/yajman')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Yajman Details</h1>
            <p className="text-gray-600">Comprehensive yajman profile and activity</p>
          </div>
        </div>
        {/* <div className="flex space-x-3">
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
            <Edit className="w-4 h-4 mr-2 inline" />
            Edit Profile
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <MessageSquare className="w-4 h-4 mr-2 inline" />
            Contact
          </button>
        </div> */}
      </div>

      {/* Profile Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
          <div className="w-full h-full bg-white rounded-full transform translate-x-32 -translate-y-32"></div>
        </div>
        <div className="relative z-10 flex items-start justify-between">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold">
                {yajman.name?.charAt(0)?.toUpperCase() || yajman.email?.charAt(0)?.toUpperCase()}
              </div>
              {yajman.isPremium && (
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Crown className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-2xl font-bold">{yajman.name || 'No name provided'}</h2>
                {yajman.isPremium && (
                  <span className="px-3 py-1 bg-yellow-500 text-yellow-900 text-sm font-medium rounded-full">
                    Premium Member
                  </span>
                )}
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${yajman.status === 'ACTIVE'
                  ? 'bg-green-500 text-green-100'
                  : yajman.status === 'INACTIVE'
                    ? 'bg-gray-500 text-gray-100'
                    : 'bg-red-500 text-red-100'
                  }`}>
                  {yajman.status}
                </span>
              </div>
              <div className="space-y-1 text-blue-100">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  {yajman.email}
                </div>
                {yajman.phone && (
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    {yajman.phone}
                  </div>
                )}
                {yajman.city && (
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    {yajman.city}
                  </div>
                )}
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Member since {format(new Date(stats.memberSince), 'MMMM yyyy')}
                </div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-blue-100 mb-1">Last Active</div>
            <div className="text-lg font-semibold">
              {yajman.lastLoginAt ? format(new Date(yajman.lastLoginAt), 'MMM dd, yyyy') : 'Never'}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: User },
              { id: 'bookings', label: 'Bookings', icon: BookOpen },
              { id: 'preferences', label: 'Preferences', icon: Heart },
              { id: 'activity', label: 'Activity', icon: Activity }
            ].map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Account Type:</span>
                      <span className="font-medium">{yajman.isPremium ? 'Premium' : 'Regular'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`font-medium ${yajman.status === 'ACTIVE' ? 'text-green-600' :
                        yajman.status === 'INACTIVE' ? 'text-gray-600' : 'text-red-600'
                        }`}>
                        {yajman.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Bookings:</span>
                      <span className="font-medium">{stats.totalBookings}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lifetime Spending:</span>
                      <span className="font-medium">₹{stats.totalSpent.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Favorite Services</h3>
                  <div className="space-y-2">
                    {stats.favoriteServices.length > 0 ? (
                      stats.favoriteServices.map((service, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium">{service}</span>
                          <Package className="w-4 h-4 text-gray-400" />
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No favorite services yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bookings' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex space-x-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search bookings..."
                      value={bookingSearch}
                      onChange={(e) => setBookingSearch(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <select
                    value={bookingFilter}
                    onChange={(e) => setBookingFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {bookingsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading bookings...</p>
                </div>
              ) : filteredBookings.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No bookings found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredBookings.map((booking) => (
                    <div key={booking.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">{booking.service.name}</h4>
                              <p className="text-sm text-gray-600">with {booking.pandit.name}</p>
                            </div>
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                              }`}>
                              {booking.status}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {format(new Date(booking.bookingDate), 'MMM dd, yyyy HH:mm')}
                            </div>
                            <div className="flex items-center">
                              <DollarSign className="w-4 h-4 mr-1" />
                              ₹{booking.totalAmount.toLocaleString()}
                            </div>
                            {booking.rating && (
                              <div className="flex items-center">
                                <Star className="w-4 h-4 mr-1 text-yellow-400 fill-current" />
                                {booking.rating}/5
                              </div>
                            )}
                          </div>
                          {booking.review && (
                            <p className="mt-2 text-sm text-gray-600 italic">"{booking.review}"</p>
                          )}
                          {booking.cancellationReason && (
                            <p className="mt-2 text-sm text-red-600">Reason: {booking.cancellationReason}</p>
                          )}
                        </div>
                        <button className="p-2 text-gray-400 hover:text-gray-600">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Preferences feature coming soon</p>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Activity timeline coming soon</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default YajmanDetails
